/*
*   @site           Preloaded: Site
*   @function       Module: Lens Wipe
*   @author         Last17
*   @copyright      Copyright 2025, Preloaded.com
*   @version        0.01
*
*********************************************************************************************/


//-----------------------------------------------
// Imports
//-----------------------------------------------

//Template
import ModuleTemplate from '../_templates/ModuleTemplate';

//Controllers
import ModuleController from '../_controllers/ModuleController';
import NavigationController from '../_controllers/NavigationController';

//Libs
import { gsap } from 'gsap';
import inView from 'in-view';



//-----------------------------------------------
// Class Definition
//-----------------------------------------------
export default class ModuleLensWipe extends ModuleTemplate {

  //-----------------------------------------------
  // Init
  //-----------------------------------------------
  init() {

    //Flags
    this.config = {
      flags: {
        textIn: false,
        modulesIn: false,
        videoPlaying: true,
        arrowComplete: false,
        noText: this.element.classList.contains('no-text')
      },
      propLast: -1
    };

    //Elements
    this.lens = this.element.querySelector('.lens svg');
    this.keyline = this.element.querySelector('.keyline svg');
    this.title = this.element.querySelector('h1');
    this.arrow = this.element.querySelector('.arrow');
    
    //Trigger points
    this.textTriggerPointOut = 0.75;
    this.contentTriggerPoint = 0.5;
    
    //Timelines
    this.initTimelines();

    //Hide modules
    this.initModules();

    //Arrow
    this.arrowInit();
    
    //Scroll events
    this.subscribeToEvent('scroll');
    this.setScrollPosition(true);

    //Text Alpha
    inView('#lens-wipe-title').on('enter', function(el) {
      if(!this.config.flags.textIn) {
        gsap.to(this.title, { opacity: 1,  duration: 0.7, ease:'quad.inOut'});
        this.config.flags.textIn = true;
      }
    }.bind(this));
    
    //Adjust slightly
    inView.threshold(0.1);

    //Fade modules in
    inView('.module').on('enter', function(el) {
      if(!el.getAttribute('data-fade-complete')) {
        gsap.to(el.querySelector('.content'), { opacity: 1,  duration: 0.7, delay:0, ease:'power1.inOut'});
        el.getAttribute('data-fade-complete', true);
      }
    }.bind(this));
  }


  //-----------------------------------------------
  // Init arrow
  //-----------------------------------------------
  arrowInit() {
    //Activate
    if(this.heroController && this.heroController.config.type === 'video' && !this.heroController.config.videoLoaded) {
      //Wait for playback
      this.heroController.player.on('play', function(data) {
        this.arrowReveal();
      }.bind(this));
      
    } else {
      //Image
      this.arrowReveal();
    }

    //Click
    this.arrow.addEventListener('click', () => {
      //Hide the nav
      if(!NavigationController.element.classList.contains('offscreen')) {
        NavigationController.displayNavBar(false);
      }

      //Animate
      gsap.to(window, {
        duration: 1,
        scrollTo: this.modules[0],
        ease: 'Power1.easeInOut',
        onComplete: () => {
        }
      });
    });
  }


  arrowReveal() {
    //Turn it on
    this.arrow.classList.add('active');

    //Animate it in
    gsap.fromTo(this.arrow, {scale: 0}, { scale: 1, opacity: 1, duration: 0.4, delay: 0, ease:'back.out'});
  }

  //-----------------------------------------------
  // Timelines
  //-----------------------------------------------
  initTimelines() {
    //Timeline: Lens
    this.timelineLensRotation = gsap.timeline({paused: true});
    this.timelineLensRotation.to(this.lens, {rotate: -7, x:'-50%', duration: 0, ease:'quad.inOut'});
    this.timelineLensRotation.to(this.lens, {rotate: 10, x:'-50%', duration: 0.8, ease:'quad.inOut'});
    this.timelineLensRotation.to(this.lens, {rotate: 10, x:'-50%', duration: 0.2});

    //Timeline: Keyline
    let keylineRotationMin = (!this.config.flags.noText) ? -11 : -6;
    let keylineRotationMax = (!this.config.flags.noText) ? -16 : -18;
    
    this.timelineKeylineRotation = gsap.timeline({paused: true});
    this.timelineKeylineRotation.to(this.keyline, {rotate: keylineRotationMin, x:'-50%', duration: 0, ease:'quad.inOut'});
    this.timelineKeylineRotation.to(this.keyline, {rotate: keylineRotationMax, x:'-50%', duration: 0.8, ease:'quad.inOut'});
    this.timelineKeylineRotation.to(this.keyline, {rotate: keylineRotationMax, x:'-50%', duration: 0.2});

    //Timeline: Arrow
    this.timelineArrow = gsap.timeline({paused: true});
    this.timelineArrow.to(this.arrow, {bottom: '5vh', duration: 0});
    this.timelineArrow.to(this.arrow, {bottom: '0vh', y:'150%', duration: 0.2, ease:'quad.in'});
    this.timelineArrow.to(this.arrow, {bottom: '0vh', y:'150%', duration: 0.8, ease:'quad.in'});
  }


  //-----------------------------------------------
  // Init modules
  //-----------------------------------------------
  initModules() {
    this.modules = [];
    let list = document.querySelectorAll('.module');
    let found = false;
    let content;

    for(let i = 0; i < list.length; i++) {
      if(list[i] === this.element) {
        //Found this element, so register everything afterwards
        list[i].setAttribute('data-fade-complete', true);
        found = true;
      } else if(found && !list[i].classList.contains('module--footer')) {
        //Find the the content block
        content = list[i].querySelector('.content');
        if(content) {
          gsap.to(content, { opacity: 0, duration: 0});
          this.modules.push(content);
        }
      } else {
        list[i].setAttribute('data-fade-complete', true);
      }
    }

    //Attempt to get the hero module from the controller
    this.heroController = ModuleController.get('hero');
  }


  //-----------------------------------------------
  // On Resize
  //-----------------------------------------------
  onResize(data) {
    //Position
    this.setScrollPosition(true);
  }


  //-----------------------------------------------
  // Set position
  //-----------------------------------------------
  setScrollPosition(force) {
    //Calculate scroll start and end points for the module
    force = (force === undefined) ? false : force;
    let scrollBounds = this.getScrollBounds();

    //If we are within the scroll bounds
    if ((window.scrollY >= scrollBounds.min && window.scrollY <= scrollBounds.max) || force || (this.config.propLast !== 1 && window.scrollY > scrollBounds.max)) {

      //Proportion of scroll
      let prop = (window.scrollY - scrollBounds.min) / (scrollBounds.max - scrollBounds.min);
      prop = Math.min(1, Math.max(0, prop)); //clamped

      //Store the position
      if(prop !== this.config.propLast) { 

        //Lens & Keyline
        if(!this.config.flags.noText) {
          this.timelineLensRotation.seek(prop);
        }
        this.timelineKeylineRotation.seek(prop);

        //Arrow (only animates forwards)
        if(prop >= this.timelineArrow.progress() || !this.config.flags.arrowComplete) {
          this.timelineArrow.seek(prop);

          //Done?
          if(!this.config.flags.arrowComplete && this.timelineArrow.progress() > 0.2) {
            this.config.flags.arrowComplete = true;
          }
        }

        //Store
        this.config.propLast = prop;

        //Attempt to stop/start the video
        if(this.heroController && this.heroController.player) {
          if(prop >= 1 && this.heroController.config.flags.videoPlaying) {
            //Pause playback
            this.heroController.player.pause();
            this.heroController.element.classList.add('invisible');
          } else if(prop < 1 && !this.heroController.config.flags.videoPlaying) {
            //Resume playback
            this.heroController.player.play();
            this.heroController.element.classList.remove('invisible');
          }
        }
      }
    }
  }


  //-----------------------------------------------
  // Scroll event
  //-----------------------------------------------
  scrollEvent(data) {
    //Parent
    super.scrollEvent(data);

    //Scroll position set
    this.setScrollPosition();
  }

}